/**
 * MobileAccountFooter — the account row pinned to the bottom of the mobile
 * burger drawer: the 'S' avatar (Settings / Logout) plus the instant theme
 * toggle beside it.
 *
 * Why it lives here and not inside <CompanyTree> any more (2026-08-01):
 * the drawer only renders the board tree on the Boards / Inbox contours, so on
 * Files, Brain, Company, Comms and Settings the tree — and with it the ONLY
 * account menu a phone has — did not render at all. Settings and Logout were
 * reachable from a board and nowhere else. The account row is shell chrome, not
 * tree content, so <Shell> renders it directly and it is present on every route.
 *
 * ADR-057 monochrome; English-only.
 */
import { Moon, Sun } from 'lucide-react';

import { useThemeStore } from '../../stores/theme-store';
import { ProfileMenu } from '../settings-v2/profile-menu';

/**
 * Instant theme toggle — the mobile peer of the desktop rail
 * <RailThemeToggle> (2026-06-18, founder spec: theme moved out of the account
 * menu). Sun when dark / Moon when light; one tap flips dark↔light.
 */
function MobileThemeToggle() {
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const isDark = theme === 'dark';
  return (
    <button
      type="button"
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Light mode' : 'Dark mode'}
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
    >
      {isDark ? <Sun className="h-4 w-4" aria-hidden /> : <Moon className="h-4 w-4" aria-hidden />}
    </button>
  );
}

export function MobileAccountFooter() {
  return (
    <div
      data-testid="mobile-account-footer"
      className="flex shrink-0 items-center justify-between gap-2 border-t border-border p-2"
    >
      <div className="min-w-0 flex-1">
        <ProfileMenu variant="flat-mobile" />
      </div>
      <MobileThemeToggle />
    </div>
  );
}
